import { store } from '@/store';
import { resetAuthToken } from '@/store/authToken/authToken.slice';
import { resetAccountsInfo } from '@/store/accountsInfo/accountsInfo.slice';
import { resetUserAccount } from 'store/userAccount/userAccount.slice.ts';
import { resetIncidents } from 'store/incidents/incidents.slice.ts';
import { resetApplicant } from 'store/applicants/applicants.slice.ts';
import {
  destroyAuthDB,
  destroyAuthorizationDatabase,
  USER_POOL,
} from 'store/authToken/authToken.thunk.ts';
import { setLoadingState } from 'store/accountsInfo/accountsInfo.actions.ts';
import { setWSConnection } from 'store/connection/connection.actions.ts';
import { resetTeam } from 'store/team/team.actions.ts';
import { resetTeams } from 'store/teams/teams.actions.ts';
import { resetMap } from 'store/map/map.action.ts';
import { teardownWebSocket } from '@/hooks/useWebSocket.ts';

export const logout = () => {
  const dispatch = store.dispatch;

  dispatch(resetAuthToken());
  dispatch(resetAccountsInfo());
  dispatch(resetUserAccount());
  dispatch(resetIncidents());
  dispatch(resetApplicant());
  dispatch(destroyAuthDB());
  sessionStorage.clear();
  localStorage.clear();
  window.location.href = '/login'; // Navigate to login page
};

/**
 * Ends a session that died on its own -- expiry, or an identity claim apiFetch
 * could not resolve -- rather than one the user chose to end. The assessment
 * PouchDB (`production_db_v2` / `test_db`) is a separate database this never
 * touches, same as `logout()`.
 *
 * Deliberately does not show a message or navigate: the caller knows why the
 * session ended and owns the message, so it can show that before sending the
 * user anywhere.
 *
 * `logout()` ends with a full page reload, which tears down every module-scoped
 * value in the bundle for free. This function does not reload -- that is the
 * point, so the caller's toast survives -- so everything the reload used to
 * clear implicitly has to be cleared explicitly here. On a shared field device
 * anything missed is inherited by the next user who signs in:
 *
 *   reload used to clear          cleared here instead
 *   ------------------------      --------------------------------------
 *   Redux (all slices)         -> the reset dispatches below
 *   useWebSocket module state  -> teardownWebSocket()
 *   connection.isWSConnected   -> setWSConnection(false)
 *   Cognito pool localStorage  -> USER_POOL.getCurrentUser()?.signOut()
 *   auth_db_v2 + its handles   -> destroyAuthorizationDatabase()
 *
 * Stays synchronous in every state effect: `QueueContainer.onSyncData` reads
 * the store immediately after the dispatch that triggered this, and an `await`
 * here would let that read run before the resets land. The one asynchronous
 * piece is fire-and-forget for that reason.
 */
export const endSession = () => {
  const dispatch = store.dispatch;

  dispatch(resetAuthToken());
  dispatch(resetAccountsInfo());
  dispatch(resetUserAccount());
  dispatch(resetIncidents());
  dispatch(resetApplicant());
  dispatch(resetTeam());
  dispatch(resetTeams());
  dispatch(resetMap());
  // `appLoading` initialises to `true` -- a first-paint default, not a teardown
  // one -- so the resetAccountsInfo() above would otherwise raise AuthPage's
  // full-screen spinner over whatever the caller is about to show.
  dispatch(setLoadingState(false));
  dispatch(setWSConnection(false));

  try {
    teardownWebSocket();
    USER_POOL.getCurrentUser()?.signOut();
  } catch (error) {
    // Best-effort: the session state above is already cleared, and throwing
    // here would replace the caller's re-auth error with this one.
    console.warn('Session teardown was incomplete', error);
  }

  destroyAuthorizationDatabase().catch((error) =>
    console.warn('Could not destroy the offline authorization database', error)
  );

  sessionStorage.clear();
};
